// src/modules/app-views/invoices/pages/Document/tabs/RecordTab/ItemsCard.jsx
// *******************************************************************************
// INVOICE ITEMS CARD
// *******************************************************************************
// Card displaying the list of invoice items (products/services).
// Allows adding, editing, and removing items via ItemModal.
// Parent Collapse header provides the add button via onActionsChange callback.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Popconfirm,
  Empty,
  Tag,
  Form,
  theme,
} from 'antd';

const { useToken } = theme;
import {
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import ItemModal from '../../modals/ItemModal';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ItemsCard = ({
  items = [],
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onActionsChange,
  storedCompany,
  issuer,
  viewMode,
  data,
}) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const { form } = useSimpleFormContext();
  const { token } = useToken();

  // 2. HOOKS & REFS
  const documentType = Form.useWatch('documentType', form);

  // 3. LOCAL VARIABLES I
  const isLocked = viewMode === 'view';
  const isAuthorized = data?.status === 'authorized';

  // 4. LOCAL STATE
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(null);

  // 5. EFFECTS

  // Expose add handler to parent for Collapse header button
  useEffect(() => {
    onActionsChange?.({ handleAddClick });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onActionsChange]);

  // 6. HANDLERS
  const handleAddClick = useCallback(() => {
    setEditingItem(null);
    setEditingIndex(null);
    setShowItemModal(true);
  }, []);

  const handleEditClick = (item, index) => {
    setEditingItem(item);
    setEditingIndex(index);
    setShowItemModal(true);
  };

  const handleModalConfirm = itemData => {
    const quantity = itemData.quantity || 1;
    const unitValue = itemData.unitValue || 0;
    const unit = itemData.unit || 'UN';

    const discountValue = itemData.discountValue || itemData.discount?.value || 0;
    const discountPercentage = itemData.discountPercentage || itemData.discount?.percentage || 0;

    const discount = itemData.discount || {
      init: discountValue > 0 ? 'value' : 'percentage',
      percentage: discountPercentage,
      value: discountValue,
    };

    let totalValue = itemData.totalValue;
    if (totalValue === undefined || totalValue === null) {
      const subtotal = financialService.roundCurrency(quantity * unitValue);
      totalValue = financialService.roundCurrency(Math.max(0, subtotal - discountValue));
    }

    const processedItem = {
      ...itemData,
      quantity,
      unitValue,
      unit,
      discount,
      discountValue,
      discountPercentage,
      totalValue,
    };

    if (editingIndex !== null) {
      onUpdateItem(editingIndex, processedItem);
    } else {
      onAddItem(processedItem);
    }
    setShowItemModal(false);
    setEditingItem(null);
    setEditingIndex(null);
  };

  const handleModalClose = () => {
    setShowItemModal(false);
    setEditingItem(null);
    setEditingIndex(null);
  };

  // 7. LOCAL VARIABLES II
  const columns = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'itemNumber',
        width: 50,
        align: 'center',
        render: (_, __, index) => index + 1,
      },
      {
        title: translateX('product'),
        dataIndex: 'name',
        ellipsis: true,
        render: (text, record) => (
          <div>
            <Text strong>{text || record.description}</Text>
            {(record.productCode || record.legacyCode) && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.productCode && (
                    <>
                      {translateX('code')}: {record.productCode}
                    </>
                  )}
                  {record.productCode && record.legacyCode && ' | '}
                  {record.legacyCode && <>LC: {record.legacyCode}</>}
                </Text>
              </div>
            )}
          </div>
        ),
      },
      {
        title: translateX('ncm'),
        dataIndex: 'ncm',
        width: 100,
        render: text => text || '-',
      },
      {
        title: translateX('quantity'),
        dataIndex: 'quantity',
        width: 80,
        align: 'right',
        render: value => financialService.formatQuantity(value || 0),
      },
      {
        title: translateX('unit_value'),
        dataIndex: 'unitValue',
        width: 110,
        align: 'right',
        render: value => financialService.formatCurrency(value || 0),
      },
      {
        title: translateX('discount'),
        dataIndex: 'discountValue',
        width: 90,
        align: 'right',
        render: (discountValue, record) => {
          const discount = record.discount;
          const value = discountValue || discount?.value || 0;
          const percentage = record.discountPercentage || discount?.percentage || 0;

          if (value === 0 && percentage === 0) return '-';

          if (percentage > 0 && (discount?.init === 'percentage' || !discount)) {
            return financialService.formatPercentage(percentage);
          }
          if (value > 0) {
            return financialService.formatCurrency(value);
          }
          return '-';
        },
      },
      {
        title: translateX('total'),
        dataIndex: 'totalValue',
        width: 120,
        align: 'right',
        render: value => (
          <Text strong style={{ color: 'token.colorSuccess' }}>
            {financialService.formatCurrency(value || 0)}
          </Text>
        ),
      },
      {
        title: translateX('actions'),
        width: 90,
        align: 'center',
        render: (_, record, index) => (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record, index)}
              disabled={isLocked || isAuthorized}
            />
            <Popconfirm
              title={translateX('remove_item_confirm')}
              onConfirm={() => onRemoveItem(index)}
              okText={translateX('yes')}
              cancelText={translateX('no')}
              disabled={isLocked || isAuthorized}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={isLocked || isAuthorized}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [isLocked, isAuthorized, onRemoveItem]
  );

  const itemsTotal = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.totalValue || 0), 0);
  }, [items]);

  // 8. UI
  return (
    <>
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={translateX('no_items_added')} />
      ) : (
        <Table
          rowKey={getRowKey}
          columns={columns}
          className="no-vertical-scroll"
          dataSource={items}
          pagination={false}
          size="small"
          scroll={{ x: 700 }}
          rowClassName={record =>
            getRowKey(record) === selectedRowKey ? 'ant-table-row-selected' : ''
          }
          onRow={(record, index) => ({
            onClick: () => setSelectedRowKey(getRowKey(record)),
            onDoubleClick: () => !isLocked && !isAuthorized && handleEditClick(record, index),
          })}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6} align="right">
                <Text strong>{translateX('items_subtotal')}:</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong style={{ color: 'token.colorSuccess', fontSize: 16 }}>
                  {financialService.formatCurrency(itemsTotal)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
            </Table.Summary.Row>
          )}
        />
      )}

      <ItemModal
        show={showItemModal}
        setShow={setShowItemModal}
        onConfirm={handleModalConfirm}
        onClose={handleModalClose}
        editingItem={editingItem}
        storedCompany={storedCompany}
        issuer={issuer}
        documentType={documentType}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ItemsCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getRowKey(record) {
  const itemNumber = record.itemNumber ?? record._id ?? Math.random();
  return `${itemNumber}-${record.productId || record._id || record.key}`;
}
